import { DatePipe } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, Injector, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FlatReportsTableComponent } from '@app/components/time-reports/flat-reports-table/flat-reports-table.component';
import { GroupedReportsComponent } from '@app/components/time-reports/grouped-reports/grouped-reports.component';
import { ReportsTableToolbarComponent } from '@app/components/time-reports/reports-table-toolbar/reports-table-toolbar.component';
import { SumUpHoursPanelComponent } from '@app/components/time-reports/sum-up-hours-panel/sum-up-hours-panel.component';
import { startMixedReportsAdd } from '@app/components/time-reports/time-report-add-dialog';
import {
  handleMixedReportsEditClosed,
  openMixedReportsEditDialog
} from '@app/components/time-reports/time-report-edit-dialog';
import { DateRangeInputComponent } from '@app/shared/components/reports/date-range-input/date-range-input.component';
import { DateRangeSelectorComponent } from '@app/shared/components/reports/date-range-selector/date-range-selector.component';
import { TimePeriodSelectorComponent } from '@app/shared/components/reports/time-period-selector/time-period-selector.component';
import { TimeReportPanelChartComponent } from '@app/shared/components/reports/time-report-panel-chart/time-report-panel-chart.component';
import { TimeReportPanelFiltersComponent } from '@app/shared/components/reports/time-report-panel-filters/time-report-panel-filters.component';
import { TimelineScrollerComponent } from '@app/shared/components/reports/timeline-scroller/timeline-scroller.component';
import { IntlDurationPipe } from '@app/shared/pipes/intl-duration.pipe';
import { AuthService } from '@app/shared/services/auth.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { TasksDataService } from '@app/shared/services/tasks-data.service';
import { TimeReportPanelFiltersService } from '@app/shared/services/time-report-panel-filters.service';
import { TimeReportRowMapper } from '@app/shared/services/time-report-row.mapper';
import { TimeReportsDataService } from '@app/shared/services/time-reports-data.service';
import { WorkTimeReportContextMapper } from '@app/shared/services/work-time-report-context.mapper';
import { DateRange } from '@app/shared/types/models/reports/new/date-range';
import { TimeReport, TimeReportStatus } from '@app/shared/types/models/reports/time-report';
import {
  GroupingModeEnum,
  ReportColumnsEnum,
  SelectedTimeReportTypeEnum,
  SortingDirection
} from '@app/shared/types/models/reports/time-report-panel.model';
import { WorkTimeReport } from '@app/shared/types/models/reports/work-time-report';
import { UiPageWrapperComponent } from '@app/shared/ui/page-wrapper/ui-page-wrapper.component';
import {
  aggregateTimeReportChartBuckets,
  buildMixedChartView,
  buildTimeReportChartCalendar,
  usesCategoryChartAxis,
  usesLegacyWorkTimeRangeChart,
  workTimeReportsFromChartRows
} from '@app/shared/utils/time-report-chart.aggregation';
import { groupTimeReportRows } from '@app/shared/utils/time-report-row.grouping';
import { aggregateTimeReportSumUp } from '@app/shared/utils/time-report-sum-up.aggregation';
import {
  collectPendingRowIds,
  compareOptionalDatesDesc,
  compareTimeReportRowsByDateDesc,
  matchesTimeReportRowFilters,
  matchesWorkTimeChartFilters,
  selectWorkTimeReports
} from '@app/shared/utils/time-report-table.utils';
import { TranslateService } from '@ngx-translate/core';
import { groupBy, uniqBy } from 'lodash';
import moment from 'moment';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';

@Component({
  imports: [
    TimePeriodSelectorComponent,
    DateRangeSelectorComponent,
    UiPageWrapperComponent,
    DateRangeInputComponent,
    TimelineScrollerComponent,
    TimeReportPanelChartComponent,
    SumUpHoursPanelComponent,
    TimeReportPanelFiltersComponent,
    FlatReportsTableComponent,
    GroupedReportsComponent,
    ReportsTableToolbarComponent
  ],
  providers: [IntlDurationPipe, DatePipe],
  selector: 'app-work-time-report',
  templateUrl: './work-time-report.html'
})
export class WorkTimeReportComponent {
  private readonly timeReportsDataService = inject(TimeReportsDataService);
  private readonly filtersService = inject(TimeReportPanelFiltersService);
  private readonly authService = inject(AuthService);
  private readonly tasksDataService = inject(TasksDataService);
  private readonly dialogService = inject(DialogService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly deviceService = inject(DeviceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly timeReportRowMapper = inject(TimeReportRowMapper);
  private readonly workTimeReportContextMapper = inject(WorkTimeReportContextMapper);

  protected readonly GroupingModeEnum = GroupingModeEnum;
  protected readonly SelectedTimeReportTypeEnum = SelectedTimeReportTypeEnum;
  protected readonly currentDateRange = computed(() => this.getCurrentSelectionRange());
  protected readonly allTimeReports = computed(() => {
    const range = this.currentDateRange();
    const reports = this.timeReportsDataService.getTimeReports(range.start, range.end)() || [];
    return this.workTimeReportContextMapper.hydrateMany(reports);
  });

  protected readonly allWorkTimeReports = computed(() => selectWorkTimeReports(this.allTimeReports()));

  protected readonly filteredWorkTimeReports = computed(() => {
    const filters = this.filtersService.appliedFilters();
    return this.allWorkTimeReports().filter(report => matchesWorkTimeChartFilters(report, filters));
  });

  protected readonly allTableRows = computed(() => this.timeReportRowMapper.mapMany(this.allTimeReports()));

  protected readonly tableRows = computed(() => {
    const filters = this.filtersService.appliedFilters();
    return [...this.allTableRows().filter(row => matchesTimeReportRowFilters(row, filters))].sort(
      compareTimeReportRowsByDateDesc
    );
  });

  protected readonly approvalInProgress = signal(false);
  protected readonly eligibleApprovalIds = computed(() => {
    const mode = this.groupingMode();
    const rows =
      mode === GroupingModeEnum.NONE
        ? this.tableRows()
        : groupTimeReportRows(this.tableRows(), mode).flatMap(group => group.rows);
    const range = this.currentDateRange();
    return collectPendingRowIds(
      rows.filter(row => {
        const source = row.source;
        if (source.id !== row.id || source.status !== TimeReportStatus.PENDING) return false;
        const start = source.dateFrom ?? source.date;
        const end = source.dateTo ?? source.date;
        return !!start && !!end && start.getTime() <= range.end.getTime() && end.getTime() >= range.start.getTime();
      })
    );
  });

  protected readonly sumUp = computed(() => aggregateTimeReportSumUp(this.tableRows()));

  protected readonly mixedChartView = computed(() => {
    const rows = this.tableRows();
    if (usesLegacyWorkTimeRangeChart(this.selectedTimeMode(), rows)) {
      return undefined;
    }
    const groupingMode = this.groupingMode();
    const calendar = usesCategoryChartAxis(groupingMode)
      ? undefined
      : buildTimeReportChartCalendar(
          this.selectedTimeMode(),
          this.selectedYear(),
          this.selectedMonth(),
          this.selectedWeek(),
          this.selectedDay(),
          this.selectedDateRange().start,
          this.selectedDateRange().end
        );
    return buildMixedChartView(aggregateTimeReportChartBuckets(rows, groupingMode, calendar), key =>
      this.translateService.instant(key)
    );
  });

  protected readonly chartWorkTimeReports = computed((): WorkTimeReport[][] => {
    if (usesLegacyWorkTimeRangeChart(this.selectedTimeMode(), this.tableRows())) {
      return [workTimeReportsFromChartRows(this.tableRows())];
    }
    return this.groupedFilteredReports();
  });

  protected readonly groupedFilteredReports = computed(() => {
    const filteredReports = this.filteredWorkTimeReports();
    const groupingMode = this.groupingMode() || GroupingModeEnum.NONE;

    const groups =
      groupingMode === GroupingModeEnum.NONE || !this.isWorkTimeChartGrouping(groupingMode)
        ? [filteredReports]
        : this.groupReportsByMode(filteredReports, groupingMode);

    const sortedGroups = groups.slice().sort((a, b) => {
      const currentUserId = this.authService.currentUser()?.id;
      const aIsCurrentUser = a[0]?.user?.()?.id === currentUserId;
      const bIsCurrentUser = b[0]?.user?.()?.id === currentUserId;
      if (aIsCurrentUser && !bIsCurrentUser) return -1;
      if (!aIsCurrentUser && bIsCurrentUser) return 1;
      const aKey = this.getGroupKey(a[0], groupingMode);
      const bKey = this.getGroupKey(b[0], groupingMode);
      return aKey.localeCompare(bKey);
    });

    return this.sortColumn() ? this.sortGroups(sortedGroups, this.sortColumn()!, this.sortDirection()) : sortedGroups;
  });

  protected readonly flattenedFilteredReports = computed(() => {
    const reports = this.filteredWorkTimeReports();
    const sortCol = this.sortColumn();

    return sortCol
      ? this.sortReports(reports, sortCol, this.sortDirection())
      : [...reports].sort((left, right) => compareOptionalDatesDesc(left.date, right.date));
  });

  protected readonly availableUsers = computed(() =>
    uniqBy(
      this.allWorkTimeReports()
        .map(report => report.user?.())
        .filter(user => user !== undefined),
      user => user.id
    )
  );
  protected readonly isExpandedAll = signal(false);
  protected readonly isMobile = this.deviceService.isMobile;
  public selectedTimeMode = signal<SelectedTimeReportTypeEnum>(SelectedTimeReportTypeEnum.WEEK);
  public selectedYear = signal<number>(moment().year());
  public selectedMonth = signal<number>(moment().month());
  public selectedWeek = signal<number>(moment().isoWeek());
  public selectedDay = signal<number>(moment().date());
  public selectedDateRange = signal<DateRange>({
    start: moment().startOf('isoWeek').toDate(),
    end: moment().endOf('isoWeek').toDate()
  });
  public groupingMode = signal<GroupingModeEnum>(
    this.deviceService.isMobile() ? GroupingModeEnum.USER : GroupingModeEnum.NONE
  );
  public sortColumn = signal<ReportColumnsEnum | null>(null);
  public sortDirection = signal<SortingDirection>(SortingDirection.ASC);
  public selectedTimeReportIds = signal<string[]>([]);
  public visibleTableColumns = signal<ReportColumnsEnum[]>([]);

  public constructor() {
    effect(() => {
      const eligible = this.eligibleApprovalIds();
      const selected = this.selectedTimeReportIds();
      const pruned = [...new Set(selected.filter(id => eligible.includes(id)))];
      if (pruned.length !== selected.length) this.selectedTimeReportIds.set(pruned);
    });

    effect(() => {
      this.filtersService.reports.set(this.allTimeReports());
      this.filtersService.tableRows.set(this.allTableRows());
      this.filtersService.timeMode.set(this.selectedTimeMode());
      this.filtersService.startDate.set(this.currentDateRange().start);
      this.filtersService.endDate.set(this.currentDateRange().end);
    });

    toObservable(this.currentDateRange, { injector: this.injector })
      .pipe(
        debounceTime(50),
        distinctUntilChanged(
          (prev, curr) => prev.start.getTime() === curr.start.getTime() && prev.end.getTime() === curr.end.getTime()
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(range => {
        this.timeReportsDataService.loadTimeReports(range.start, range.end);
      });
  }

  private revalidateApprovalIds(ids: string[]): string[] {
    const represented = new Set(this.eligibleApprovalIds());
    const mode = this.groupingMode();
    const range = this.currentDateRange();
    const filters = this.filtersService.appliedFilters();
    // Hydrated table rows may be cached copies. Read canonical records again at dispatch time.
    const canonicalReports = new Map(
      (this.timeReportsDataService.getTimeReports(range.start, range.end)() ?? []).map(report => [report.id, report])
    );
    return [...new Set(ids)].filter(id => {
      const report = canonicalReports.get(id);
      if (!represented.has(id) || !report || report.status !== TimeReportStatus.PENDING) return false;
      const start = report.dateFrom ?? report.date;
      const end = report.dateTo ?? report.date;
      if (!start || !end || !(start.getTime() <= range.end.getTime() && end.getTime() >= range.start.getTime()))
        return false;
      const row = this.timeReportRowMapper.map(this.workTimeReportContextMapper.hydrate(report));
      return (
        matchesTimeReportRowFilters(row, filters) &&
        (mode === GroupingModeEnum.NONE || groupTimeReportRows([row], mode).length > 0)
      );
    });
  }

  private getCurrentSelectionRange(): DateRange {
    const mode = this.selectedTimeMode();
    const year = this.selectedYear();
    const month = this.selectedMonth();

    switch (mode) {
      case SelectedTimeReportTypeEnum.DAY:
        return {
          start: moment({ year, month, day: this.selectedDay() }).startOf('day').toDate(),
          end: moment({ year, month, day: this.selectedDay() }).endOf('day').toDate()
        };
      case SelectedTimeReportTypeEnum.WEEK:
        return {
          start: moment().year(year).isoWeek(this.selectedWeek()).startOf('isoWeek').toDate(),
          end: moment().year(year).isoWeek(this.selectedWeek()).endOf('isoWeek').toDate()
        };
      case SelectedTimeReportTypeEnum.MONTH:
        return {
          start: moment({ year, month }).startOf('month').toDate(),
          end: moment({ year, month }).endOf('month').toDate()
        };
      case SelectedTimeReportTypeEnum.YEAR:
        return {
          start: moment({ year }).startOf('year').toDate(),
          end: moment({ year }).endOf('year').toDate()
        };
      case SelectedTimeReportTypeEnum.RANGE:
        return this.selectedDateRange();
      default:
        return {
          start: moment().startOf('day').toDate(),
          end: moment().endOf('day').toDate()
        };
    }
  }

  private getGroupKey(report: WorkTimeReport, mode: GroupingModeEnum): string {
    switch (mode) {
      case GroupingModeEnum.USER:
        return report.user?.()?.fullName || '';
      case GroupingModeEnum.DATE:
        return moment(report.date).format('YYYY-MM-DD');
      case GroupingModeEnum.USER_DATE:
        return `${report.user?.()?.fullName || ''}_${moment(report.date).format('YYYY-MM-DD')}`;
      case GroupingModeEnum.TASK:
        return report.task?.name || '';
      case GroupingModeEnum.PROJECT:
        return report.project?.name || '';
      default:
        return '';
    }
  }

  protected onSortChanged(sort: { direction: SortingDirection; column: ReportColumnsEnum }): void {
    this.sortColumn.set(sort.column);
    this.sortDirection.set(sort.direction);
  }

  private sortReports(
    reports: WorkTimeReport[],
    column: ReportColumnsEnum,
    direction: SortingDirection
  ): WorkTimeReport[] {
    return [...reports].sort((a: WorkTimeReport, b: WorkTimeReport): number => {
      const aValue = this.getSortValue(a, column);
      const bValue = this.getSortValue(b, column);

      let result = 0;
      if (aValue < bValue) result = -1;
      if (aValue > bValue) result = 1;

      return direction === SortingDirection.ASC ? result : -result;
    });
  }

  private sortGroups(
    groups: WorkTimeReport[][],
    column: ReportColumnsEnum,
    direction: SortingDirection
  ): WorkTimeReport[][] {
    return [...groups].sort((a: WorkTimeReport[], b: WorkTimeReport[]): number => {
      const aValue = this.getGroupSortValue(a, column);
      const bValue = this.getGroupSortValue(b, column);

      let result = 0;
      if (aValue < bValue) result = -1;
      if (aValue > bValue) result = 1;

      return direction === SortingDirection.ASC ? result : -result;
    });
  }

  private getSortValue(report: WorkTimeReport, column: ReportColumnsEnum): string | number {
    switch (column) {
      case ReportColumnsEnum.DATE:
        return moment(report.date).valueOf();
      case ReportColumnsEnum.USER:
        return report.user?.()?.fullName || '';
      case ReportColumnsEnum.TASK_ID:
        return report.task?.taskNumber || 0;
      case ReportColumnsEnum.TASK_TITLE:
        return report.task?.name || '';
      case ReportColumnsEnum.PROJECT:
        return report.project?.name || '';
      case ReportColumnsEnum.LOCATION:
        return report.location?.name || '';
      case ReportColumnsEnum.DESCRIPTION:
        return report.description || '';
      case ReportColumnsEnum.TYPE:
        return report.hoursType || '';
      case ReportColumnsEnum.TOTAL:
        return moment.duration(report.duration).asMinutes();
      case ReportColumnsEnum.ESTIMATION:
        return report.task?.estimation?.time || 0;
      default:
        return 0;
    }
  }

  private getGroupSortValue(group: WorkTimeReport[], column: ReportColumnsEnum): string | number {
    const report = group[0];

    if (column === ReportColumnsEnum.TOTAL) {
      return group.reduce((sum, r) => sum + moment.duration(r.duration).asMinutes(), 0);
    }

    return this.getSortValue(report, column);
  }

  private isWorkTimeChartGrouping(mode: GroupingModeEnum): boolean {
    return (
      mode === GroupingModeEnum.USER ||
      mode === GroupingModeEnum.DATE ||
      mode === GroupingModeEnum.USER_DATE ||
      mode === GroupingModeEnum.TASK ||
      mode === GroupingModeEnum.PROJECT
    );
  }

  private groupReportsByMode(reports: WorkTimeReport[], mode: GroupingModeEnum): WorkTimeReport[][] {
    const groupKeys = {
      [GroupingModeEnum.USER]: (r: WorkTimeReport): string => r.user?.()?.id || 'unknown',
      [GroupingModeEnum.DATE]: (r: WorkTimeReport): string => moment(r.date).format('YYYY-MM-DD'),
      [GroupingModeEnum.USER_DATE]: (r: WorkTimeReport): string =>
        `${r.user?.()?.id || 'unknown'}_${moment(r.date).format('YYYY-MM-DD')}`,
      [GroupingModeEnum.TASK]: (r: WorkTimeReport): string => r.taskId || 'unknown',
      [GroupingModeEnum.PROJECT]: (r: WorkTimeReport): string => r.projectId || 'unknown'
    };

    return Object.values(groupBy(reports, groupKeys[mode]));
  }

  protected selectTimeReport(id: string): void {
    if (this.approvalInProgress() || !this.eligibleApprovalIds().includes(id)) return;
    const currentIds = this.selectedTimeReportIds();
    const newIds = currentIds.includes(id) ? currentIds.filter(item => item !== id) : [...currentIds, id];
    this.selectedTimeReportIds.set(newIds);
  }

  protected openTaskDetails(event: Event, taskId: string): void {
    event.stopPropagation();
    const task = this.tasksDataService.getById(taskId);
    if (task) {
      window.open(task().url(), '_blank');
    }
  }

  protected openTaskDetailsById(taskId: string): void {
    const task = this.tasksDataService.getById(taskId);
    if (task) {
      window.location.href = task().url();
    }
  }

  protected changeGroupingMode(groupingMode: GroupingModeEnum): void {
    this.groupingMode.set(groupingMode);
    this.isExpandedAll.set(false);
  }

  protected expandAllSessions(): void {
    this.isExpandedAll.set(true);
  }

  protected hideAllSessions(): void {
    this.isExpandedAll.set(false);
  }

  protected clearFilters(): void {
    this.filtersService.resetFilters();
  }

  protected addReport(): void {
    const range = this.currentDateRange();
    startMixedReportsAdd(
      this.dialogService,
      { start: range.start as Date, end: range.end as Date },
      this.timeReportsDataService
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  protected onEditReport(report: TimeReport): void {
    const range = this.currentDateRange();
    const dialogRef = openMixedReportsEditDialog(this.dialogService, report, range);
    if (!dialogRef) {
      return;
    }

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          handleMixedReportsEditClosed(report, result, range, this.timeReportsDataService);
        }
      });
  }

  protected onDeleteReport(report: TimeReport): void {
    if (!report.id) {
      return;
    }

    const reportId = report.id;
    this.dialogService
      .openConfirmationDialog({
        title: this.translateService.instant('time-reports.confirmation.delete-header'),
        desc: this.translateService.instant('time-reports.confirmation.delete-description')
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          if (result) {
            const range = this.currentDateRange();
            this.timeReportsDataService.deleteTimeReport(reportId, range.start, range.end).subscribe();
          }
        }
      });
  }

  protected onSelectedTimeReportIdsChanged(ids: string[]): void {
    if (this.approvalInProgress()) return;
    this.selectedTimeReportIds.set([...new Set(ids.filter(id => this.eligibleApprovalIds().includes(id)))]);
  }

  protected approveSelectedReports(): void {
    if (this.approvalInProgress()) return;
    const selectedIds = this.selectedTimeReportIds();
    const reportIds = this.revalidateApprovalIds(selectedIds);
    this.warnAboutSkippedApprovals(selectedIds, reportIds);
    // Consume this selection before dispatch, so callbacks cannot clear selection from another query.
    this.selectedTimeReportIds.set([]);
    if (reportIds.length === 0) return;

    const range = this.currentDateRange();
    const batchRange = { start: new Date(range.start), end: new Date(range.end) };
    this.approvalInProgress.set(true);
    this.timeReportsDataService
      .approveTimeReports(reportIds)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.approvalInProgress.set(false);
          // The root-provided service owns this read, so it also survives component teardown.
          this.timeReportsDataService.reloadTimeReports(batchRange.start, batchRange.end);
        })
      )
      .subscribe({
        // The data service reports the failure; finalization refreshes without retrying writes.
        error: () => undefined
      });
  }

  private warnAboutSkippedApprovals(selectedIds: string[], reportIds: string[]): void {
    const skippedCount = new Set(selectedIds.filter(id => !reportIds.includes(id))).size;
    if (skippedCount > 0) {
      this.toastService.warning(
        this.translateService.instant('time-reports.toasts.approval-skipped', { count: skippedCount })
      );
    }
  }
}
